/**
 * **A01 · The count sheet**, **A02 · the bag tag and the seed receipt** and **A03 · the deposit
 * slip cover** (Stream A, Paper.dc.html), from the count room's bags, registers, the bags' custody,
 * counts and deposits, and the day's reconciliation. One bag, one sheet: two counters write the
 * same numbers and the gap line is the sheet's point, so what the counters write stays a blank to
 * write on ("$"); what the books hold prints, and what they do not prints NO DATA. The tag goes on
 * the bag at issue; its QR is the bag number everywhere after. The seed receipt stays with the
 * chair who took the money. The deposit cover goes in the night bag on top of the bank's slip.
 */
import { dollars } from './dollars.js';
import { NO_DATA, type PaperCell, type PaperPage } from './paper_page.js';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DENOMS = ['$100', '$50', '$20', '$10', '$5', '$2', '$1', 'Coin', 'Checks'];

/** `2027-09-11` → `Saturday Sep 11` (short: `Sat Sep 11`). */
export const festivalDayWords = (ymd: string, short = false): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return NO_DATA;
  const day = DAYS[new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()];
  const name = short ? (day ?? '').slice(0, 3) : (day ?? '');
  return `${name} ${MONTHS[Number(m[2]) - 1] ?? ''} ${String(Number(m[3]))}`;
};
/** `$200` for a whole-dollar seed, `$200.50` otherwise. */
const seedWords = (cents: number): string => dollars(cents).replace(/\.00$/, '');
const fig = (c: number | null): PaperCell => c ?? NO_DATA;

export interface CountSheetInput {
  bag: string;
  area: string | null;
  register: string | null;
  /** The pickup that brought the bag in; null before one. `by` is the runner, when named. */
  pickedUp: { day: string; time: string; by: string | null } | null;
  /** The reader's cash for the register that day, from the Square pull; null when not pulled. */
  squareCashCents: number | null;
  /** What went out with the bag. */
  seedCents: number | null;
}

export function countSheet(i: CountSheetInput): PaperPage {
  const p = i.pickedUp;
  const where = [i.area ?? `area ${NO_DATA}`, p ? festivalDayWords(p.day) : null, i.register]
    .filter((x): x is string => !!x)
    .join(' · ');
  const picked = p ? `picked up ${p.time} by ${p.by ?? NO_DATA}` : 'not picked up yet';
  return {
    title: 'Count sheet',
    sub: `Bag ${i.bag} · ${where} · ${picked}`,
    qr: `BAG ${i.bag}`,
    blocks: [
      {
        head: 'BILLS AND COIN',
        cols: '1fr 90px 90px 90px',
        rows: [['', 'COUNT', 'COUNTER 1', 'COUNTER 2'], ...DENOMS.map((d) => [d, '', '', ''])],
      },
      {
        head: 'TOTALS',
        cols: '1fr 120px',
        rows: [
          ['Counted · counter 1', '$'],
          ['Counted · counter 2', '$'],
          [
            `Square cash sales · ${i.register ?? 'the register'} · from the pull`,
            fig(i.squareCashCents),
          ],
          ['Seed issued with this bag', fig(i.seedCents)],
          ['THE GAP · counted − (Square cash + seed)', '$'],
        ],
      },
    ],
    sign: ['Counter 1 · print and sign', 'Counter 2 · print and sign'],
    foot: `Count sheet · bag ${i.bag} · if the two counts differ, count again before signing`,
  };
}

export interface BagTagInput {
  bag: string;
  area: string | null;
  register: string | null;
  seedCents: number | null;
  /** The issue: its day, its time in words, who issued it and the chair who took it. */
  issued: { day: string; at: string; by: string | null; to: string | null } | null;
}

/** The tag (3 × 2) and the seed receipt (4 × 6), in that order. */
export function bagTag(i: BagTagInput): PaperPage[] {
  const s = i.issued;
  const seed = i.seedCents === null ? NO_DATA : seedWords(i.seedCents);
  const head = [i.area ?? `area ${NO_DATA}`, i.register, s ? festivalDayWords(s.day, true) : null]
    .filter((x): x is string => !!x)
    .join(' · ');
  const when = s ? `${festivalDayWords(s.day, true)} · ${s.at}` : 'not issued yet';
  return [
    {
      size: 'tag',
      title: 'Bag tag',
      sub: head,
      qr: `BAG ${i.bag}`,
      big: i.bag,
      bigSub: s
        ? `Issued ${s.at} · ${s.by ?? NO_DATA}, count room · seed ${seed}`
        : `Not issued yet · seed ${seed}`,
      blocks: [],
      foot: `Bag ${i.bag}`,
    },
    {
      size: 'card',
      title: 'Seed receipt',
      sub: 'The chair keeps this. The count room keeps the other half.',
      qr: `SEED ${i.bag}`,
      blocks: [
        {
          head: '',
          cols: '1fr 1fr',
          rows: [
            ['Area', i.area ?? NO_DATA],
            ['Register', i.register ?? NO_DATA],
            ['Bag', i.bag],
            ['Seed amount', fig(i.seedCents)],
            ['Issued', when],
            ['Issued by', s ? `${s.by ?? NO_DATA} · count room` : ''],
            ['Taken by', s?.to ?? ''],
          ],
        },
      ],
      sign: ['Issued by', 'Taken by'],
      foot: `Seed receipt · bag ${i.bag}`,
    },
  ];
}

export interface DepositCoverInput {
  /** The deposit's festival day (YYYY-MM-DD); null when it has none. */
  day: string | null;
  /** The bank it goes to, from Money's bank accounts: `Chase operating · 4099`. */
  bank: string | null;
  /** The bank bag's serial, which is the slip CIDER matches to the statement; null until sealed. */
  slip: string | null;
  /** Each bag in the deposit: its number, area and register, what was counted, the counters. */
  bags: {
    bag: string;
    area: string | null;
    register: string | null;
    cents: number;
    counters: string[];
  }[];
  /** The deposit's sums; null until it is counted. */
  totals: {
    currencyCents: number;
    coinCents: number;
    checkCents: number;
    totalCents: number;
  } | null;
  /** The deposit's id, for the QR before a slip is written. */
  id: string;
}

/** `Dale Pelc` → `DP`. */
const initials = (name: string): string =>
  name
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

/** A03 · the deposit slip cover: every bag by number, the totals, two names, the slip. */
export function depositCover(i: DepositCoverInput): PaperPage {
  const m = i.day ? /^(\d{4})/.exec(i.day) : null;
  const day = i.day && m ? `${festivalDayWords(i.day)}, ${m[1] ?? ''}` : NO_DATA;
  const slip = i.slip ? `slip ${i.slip}` : 'not sealed yet';
  const t = i.totals;
  return {
    title: 'Deposit · night bag',
    sub: `${day} · ${i.bank ?? `bank ${NO_DATA}`} · ${slip}`,
    qr: `DEP ${i.slip ?? i.id}`,
    blocks: [
      {
        head: 'BAGS IN THIS DEPOSIT',
        cols: '70px 1fr 110px 120px',
        rows: [
          ['BAG', 'AREA · REGISTER', 'COUNTED', 'COUNTED BY'],
          ...(i.bags.length
            ? i.bags.map((b) => [
                b.bag,
                [b.area ?? NO_DATA, b.register].filter(Boolean).join(' · '),
                b.cents,
                b.counters.length ? b.counters.map(initials).join(' · ') : NO_DATA,
              ])
            : [['', 'No bag is on this deposit yet', '', '']]),
        ],
      },
      {
        head: 'TOTALS',
        cols: '1fr 140px',
        rows: [
          ['Currency', fig(t?.currencyCents ?? null)],
          ['Coin', fig(t?.coinCents ?? null)],
          ['Checks', fig(t?.checkCents ?? null)],
          ['DEPOSIT TOTAL', fig(t?.totalCents ?? null)],
        ],
      },
    ],
    sign: ['Sealed by · name and time', 'Carried by · name and time'],
    foot: `Deposit cover · ${slip} · the bank’s slip is underneath`,
  };
}
